import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { formatInr } from "../../utils/financeFormat";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "./TableTotalAmount";
import MonthlyBankCashHero from "./MonthlyBankCashHero";
import MonthlyCashPaymentPanel from "./MonthlyCashPaymentPanel";
import { MonthlyMetricTrend } from "./MonthlyCostTabShell";

const MEAT_SECTIONS = [
  { key: "chicken", label: "Chicken", bankSlot: true, cashSlot: "chicken" },
  { key: "mutton", label: "Mutton", cashSlot: "mutton" },
  { key: "seafood", label: "Sea Food", cashSlot: "seafood" },
];

export default function MeatMonthlyTab({
  periodMonth,
  comparison,
  overview,
  cashBySlot = {},
  cashLinesBySlot = {},
  cashSaving = false,
  onAddCashLine,
  onDeleteCashLine,
}) {
  const [section, setSection] = useState("chicken");

  const chickenBank = Number(overview?.chickenRupee) || 0;
  const chickenCash = Number(overview?.chickenCashRupee) || Number(cashBySlot.chicken) || 0;
  const mutton = Number(overview?.muttonCashRupee) || Number(cashBySlot.mutton) || 0;
  const seafood = Number(overview?.seafoodCashRupee) || Number(cashBySlot.seafood) || 0;
  const meatTotal =
    Number(overview?.meatTotalRupee) || chickenBank + chickenCash + mutton + seafood;
  const chickenRows = overview?.chickenDebitRows || [];

  return (
    <>
      <MonthlyMetricTrend
        metricLabel="Meat (total)"
        valueFn={(m) => m.meatTotalRupee}
        comparison={comparison}
      />
      <MonthlyBankCashHero
        label="Meat total"
        periodMonth={periodMonth}
        bankRupee={chickenBank}
        cashRupee={mutton + seafood + chickenCash}
        extraMeta={
          <>
            <div>
              Combined meat: <strong className="text-light">{formatInr(meatTotal)}</strong>
            </div>
            <div>Mutton + Sea Food + Chicken cash</div>
          </>
        }
      />

      <div className="d-flex flex-wrap gap-2 mb-3">
        {MEAT_SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`btn btn-sm ${section === s.key ? "btn-info" : "btn-outline-secondary"}`}
            onClick={() => setSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "chicken" ? (
        <>
          <p className="text-secondary small mb-2">
            Bank keyword: <code className="text-info">7013834052</code> — plus any cash paid in addition.
          </p>
          <MonthlyBankCashHero
            label="Chicken"
            periodMonth={periodMonth}
            bankRupee={chickenBank}
            cashRupee={chickenCash}
          />
          <MonthlyCashPaymentPanel
            slotId="chicken"
            label="Chicken — cash payment"
            hint="Added to meat total and monthly cash rollup."
            lines={cashLinesBySlot.chicken || []}
            totalRupee={cashBySlot.chicken ?? chickenCash}
            saving={cashSaving}
            onAddLine={onAddCashLine}
            onDeleteLine={onDeleteCashLine}
          />
          <div className="finance-recon-scroll">
            <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Bank</th>
                  <th>Category</th>
                  <th>Particulars</th>
                  <th className="text-end">Amount ₹</th>
                </tr>
              </thead>
              <tbody>
                {chickenRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-secondary small">
                      No chicken vendor debits this month — upload bank statements.
                    </td>
                  </tr>
                ) : (
                  <>
                    {chickenRows.map((r, idx) => (
                      <tr key={`ch-${r.txnDateIso}-${idx}`}>
                        <td>{r.txnDateIso}</td>
                        <td>{r.bank}</td>
                        <td>{r.category || "—"}</td>
                        <td className="small">{r.particulars}</td>
                        <td className="text-end text-warning fw-semibold">{formatInr(r.debitRupee)}</td>
                      </tr>
                    ))}
                    <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                      <td colSpan={4}>Bank total ({chickenRows.length} lines)</td>
                      <td className="text-end fs-6">
                        <TableTotalAmount amount={chickenBank} toneClass="text-warning" />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </>
      ) : null}

      {section === "mutton" ? (
        <>
          <p className="text-secondary small mb-2">Mutton is paid in cash — enter the monthly cash amount below.</p>
          <MonthlyBankCashHero label="Mutton" periodMonth={periodMonth} bankRupee={0} cashRupee={mutton} />
          <MonthlyCashPaymentPanel
            slotId="mutton"
            label="Mutton — cash payment"
            hint="Counted in monthly cash total and meat total."
            lines={cashLinesBySlot.mutton || []}
            totalRupee={cashBySlot.mutton ?? mutton}
            saving={cashSaving}
            onAddLine={onAddCashLine}
            onDeleteLine={onDeleteCashLine}
          />
        </>
      ) : null}

      {section === "seafood" ? (
        <>
          <p className="text-secondary small mb-2">Sea food is paid in cash — enter the monthly cash amount below.</p>
          <MonthlyBankCashHero label="Sea Food" periodMonth={periodMonth} bankRupee={0} cashRupee={seafood} />
          <MonthlyCashPaymentPanel
            slotId="seafood"
            label="Sea Food — cash payment"
            hint="Counted in monthly cash total and meat total."
            lines={cashLinesBySlot.seafood || []}
            totalRupee={cashBySlot.seafood ?? seafood}
            saving={cashSaving}
            onAddLine={onAddCashLine}
            onDeleteLine={onDeleteCashLine}
          />
        </>
      ) : null}
    </>
  );
}
